import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  X, Search, CheckCircle2, Loader2, Building2, UserRound,
  Phone, Mail, ExternalLink, ChevronDown, ChevronUp,
  CalendarClock, FileText, ArrowLeft, StickyNote,
  Briefcase, UserPlus, FilePlus, Paperclip, Zap,
  Package, PoundSterling,
} from 'lucide-react';
import { toast } from 'sonner';
import { AddProspectAccountInline } from './AddProspectAccountInline';
import { AddToCrmModal } from './AddToCrmModal';
import { AddCallToCrmModal } from './AddCallToCrmModal';
import RecentQuotesPicker from './RecentQuotesPicker';
import QuoteVersionHistoryModal from './QuoteVersionHistoryModal';
import CustomerContactModal from './CustomerContactModal';
import CustomerQuotesPanel from './CustomerQuotesPanel';
import CustomerOrdersPanel from './CustomerOrdersPanel';
import CustomerPricingPanel from './CustomerPricingPanel';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { QuoteStagePill as StagePill } from './QuoteStagePill';
import { useV3Shell } from '../v3/V3ShellContext';
import { useV3Modal } from '../v3/useV3Modal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORANGE = '#ee7203';
const fieldCardClass = 'rounded-xl border border-gray-200 bg-white shadow-sm p-3';
const inputClass =
  'rounded-lg border-2 border-gray-300 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 h-10';
const headerBtnClass =
  'inline-flex items-center gap-1.5 rounded-xl h-9 px-3 border-2 bg-white text-xs font-bold whitespace-nowrap transition-all shadow-sm hover:shadow-md active:scale-95';

// Side tabs down the left edge of the modal — more can be added here later.
const SIDE_TABS = [
  { id: 'quotes', label: 'Quotes', Icon: FileText },
  { id: 'orders', label: 'Orders', Icon: Package },
  { id: 'pricing', label: 'Pricing', Icon: PoundSterling },
];

// Layout: [tab rail][Customer Management][slide-out panel]. When the panel is open, the modal and the panel become two
// EQUAL halves of the space to the right of the rail — same width, side by side. When the panel is closed, the modal is
// capped at MODAL_MAX_W as before.
const MODAL_MAX_W = 1000;
const RAIL_W = 40;
const SHELL_GAP = 12;
const HALF_WIDTH = `calc((100% - ${RAIL_W + SHELL_GAP}px) / 2)`;

// ── Helper: format ISO date to "12 Jan 26" ──────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: '2-digit',
    });
  } catch (_) { return iso; }
}

// ── NoteCard ─────────────────────────────────────────────────────────────────
function NoteCard({ note }) {
  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50/80 p-3 space-y-1">
      {note.title && note.title !== 'Note from C30' && (
        <div className="text-xs font-bold text-gray-700">{note.title}</div>
      )}
      <div className="text-sm text-gray-800 whitespace-pre-wrap leading-snug">{note.content}</div>
      <div className="text-[11px] text-gray-400 flex items-center gap-2 mt-0.5">
        {note.owner_name && <span>{note.owner_name}</span>}
        {note.created_at && <span>{fmtDate(note.created_at)}</span>}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
// `resumeSnapshot` (optional) — passed only by ActivityModalHost when reopening
// this modal from the Activity Bar "Open"/"Recent" tabs (see Modal UI v2). When
// present, state is restored from it instead of the normal fresh-open flow.
//
// Every entry point (global search, Sales / CRM, Sales Hub, the Activity Bar) renders THIS component, so they all look and
// behave the same: it sits under the sub-header, runs to the bottom of the screen, and — when opened with an account
// (`initialAccount`) or a customer name (`initialSearch`) — goes straight to that customer.
export const ContactNotesTasksModal = ({ isOpen, onClose, initialAccount = null, initialSearch = null, resumeSnapshot = null, embeddedFromQuote = false, initialOrder = null, transportOrderNumbers = null }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const v3Shell = useV3Shell();
  // View: 'search' | 'detail' | 'task'
  const [view, setView] = useState('search');

  // Account search
  const [accountQuery, setAccountQuery] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAddAccount, setShowAddAccount] = useState(false);

  // Loaded data for selected account
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [qp, setQp] = useState({ quotes: [], projects: [] });
  const [qpLoading, setQpLoading] = useState(false);

  // Add Note
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Contact add / edit modal
  const [contactModal, setContactModal] = useState({ open: false, contact: null });

  // Side tabs + slide-out panel
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelTab, setPanelTab] = useState('quotes');
  // UI V3: tells the V3 shell how much width this window wants (the tab rail and the window, or with the panel open both halves), so the shell
  // can push the side bars shut when the room between them is short. It does nothing outside the V3 shell.
  useV3Modal(isOpen, panelOpen ? 1600 : 1040);
  const [me, setMe] = useState(null);
  // Opened from a search for an order number: once the customer is open, show the Orders tab with this order opened.
  const [focusOrder, setFocusOrder] = useState(null); // { orderId, orderNumber } or null
  useEffect(() => {
    if (focusOrder && selectedAccount && view === 'detail') {
      setPanelTab('orders');
      setPanelOpen(true);
    }
  }, [focusOrder, selectedAccount, view]);

  // Task form
  const [taskSubject, setTaskSubject] = useState('');
  const [taskDesc, setTaskDesc] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskPriority, setTaskPriority] = useState('Normal');
  const [taskStatus, setTaskStatus] = useState('Not Started');
  const [savingTask, setSavingTask] = useState(false);
  // Task owner + related-to
  const [taskOwnerId, setTaskOwnerId] = useState('');
  const [taskRelatedType, setTaskRelatedType] = useState('account'); // 'account' | 'quote' | 'project'
  const [taskRelatedId, setTaskRelatedId] = useState('');
  const [zohoUsers, setZohoUsers] = useState([]);
  const [zohoUsersLoading, setZohoUsersLoading] = useState(false);

  // Completion result banner
  const [completionResult, setCompletionResult] = useState(null);

  // Local quote stage overrides (quoteId → newStage) so UI updates instantly
  const [quoteStageOverrides, setQuoteStageOverrides] = useState({});

  // Embedded AddToCrmModal (Add Quote / Add Project from Customer Management)
  const [showCrmModal, setShowCrmModal] = useState(false);
  const [crmModalMode, setCrmModalMode] = useState(null); // 'quote' | 'project'
  const [historyItem, setHistoryItem] = useState(null);

  // Quick-add: quote selected from the top-bar picker
  const [pendingQuote, setPendingQuote] = useState(null); // { quoteForm, orderNum, customerName }
  const [crmQuoteForm, setCrmQuoteForm] = useState(null); // passed to AddToCrmModal for smart-add

  // Add Quote (orange button) — opens the same Create Quote modal used from the dashboard's "Create New" Quotes pill,
  // pre-filled with this customer's name, address and CRM account id. Lazy-loaded once so it's ready to open instantly.
  const [QuotesMod, setQuotesMod] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quotePrefillAccount, setQuotePrefillAccount] = useState(null);
  const [quotePrefillItems, setQuotePrefillItems] = useState(null);
  // A quote opened by Edit on a card in the Quotes panel, and a counter that goes up every time a quote window closes so the
  // Quotes and Pricing panels behind it reload and show what the quote changed.
  const [quoteInitial, setQuoteInitial] = useState(null);
  const [quoteTick, setQuoteTick] = useState(0);
  useEffect(() => {
    let alive = true;
    import('../pages/Quotes').then((m) => { if (alive) setQuotesMod(() => m.default); });
    return () => { alive = false; };
  }, []);

  // Add Call — opens the same Add Call to CRM modal used elsewhere, pre-filled with this customer.
  const [showAddCallModal, setShowAddCallModal] = useState(false);

  const handleAddQuote = async () => {
    if (!selectedAccount?.id) return;
    let address = '';
    try {
      const res = await api.get(`${API}/zoho/accounts/${selectedAccount.id}/fresh`);
      address = res.data?.address || '';
    } catch (_) { /* the quote still opens, just without a pre-filled address */ }
    if (!me) {
      try { const mres = await api.get(`${API}/auth/me`); setMe(mres.data); } catch (_) { /* non-fatal */ }
    }
    setQuotePrefillAccount({ id: selectedAccount.id, name: selectedAccount.name, address });
    setShowQuoteModal(true);
  };
  // Create Quote from the Pricing tab: the same Create Quote modal as the orange Add Quote button (customer name, address and
  // CRM account id filled in), opened with the products ticked on the customer's price list already on the quote at the
  // customer's own prices.
  const handleCreateQuoteFromPriceList = async (selectedPrices) => {
    if (!selectedAccount?.id || !Array.isArray(selectedPrices) || selectedPrices.length === 0) return;
    let address = '';
    try {
      const res = await api.get(`${API}/zoho/accounts/${selectedAccount.id}/fresh`);
      address = res.data?.address || '';
    } catch (_) { /* the quote still opens, just without a pre-filled address */ }
    if (!me) {
      try { const mres = await api.get(`${API}/auth/me`); setMe(mres.data); } catch (_) { /* non-fatal */ }
    }
    setQuotePrefillItems(selectedPrices.map((p) => ({
      code: p.code,
      description: p.description || '',
      unit_price: p.unit_price,
      unit: p.unit || 'Each',
      price_date: p.price_date || null,
      source: p.source || null,
      line_meta: p.line_meta || null,
    })));
    setQuotePrefillAccount({ id: selectedAccount.id, name: selectedAccount.name, address });
    setShowQuoteModal(true);
  };
  // Edit on a card in the Quotes panel: the quote opens in the same merged Create Quote window the Add Quote button opens, and
  // closing it comes straight back here, to this panel.
  const handleOpenQuoteFromPanel = async (picked) => {
    if (embeddedFromQuote) {
      toast.info('Close Customer Management to get back to your quote first');
      return;
    }
    if (!me) {
      try { const mres = await api.get(`${API}/auth/me`); setMe(mres.data); } catch (_) { /* non-fatal */ }
    }
    setQuotePrefillAccount(null);
    setQuotePrefillItems(null);
    setQuoteInitial(picked);
    setShowQuoteModal(true);
  };
  const handleSmartAddQuote = () => {
    if (!pendingQuote) return;
    setCrmModalMode('quote');
    setCrmQuoteForm(pendingQuote.quoteForm);
    setShowCrmModal(true);
  };
  const handleCrmModalClose = () => {
    setShowCrmModal(false);
    setCrmQuoteForm(null);
    // Refresh quotes/projects after adding
    if (selectedAccount?.id) {
      api.get(`${API}/zoho/account/${selectedAccount.id}/quotes-and-projects`)
        .then(r => setQp(r.data))
        .catch(() => {});
    }
  };

  /** Called when user clicks a card in the top-bar RecentQuotesPicker */
  const handleSelectRecentQuoteForSearch = (quoteForm) => {
    const customerName = quoteForm?.customer_name || '';
    const orderNum = quoteForm?.quote_number || quoteForm?.reference || quoteForm?.order_number || '';
    setPendingQuote({ quoteForm, orderNum, customerName });
    if (customerName) {
      setAccountQuery(customerName);
      runAccountSearch(customerName);
    }
  };

  const handleQuoteStageChange = (quoteId, newStage) => {
    setQuoteStageOverrides(prev => ({ ...prev, [quoteId]: newStage }));
  };

  const openPanel = (tab) => {
    if (panelOpen && panelTab === tab) { setPanelOpen(false); return; }
    setPanelTab(tab);
    setPanelOpen(true);
    if (!me) {
      api.get(`${API}/auth/me`).then(r => setMe(r.data)).catch(() => {});
    }
  };

  const resetAll = () => {
    setView('search');
    setAccountQuery('');
    setAccounts([]);
    setSelectedAccount(null);
    setShowAddAccount(false);
    setContacts([]);
    setNotes([]);
    setNotesExpanded(false);
    setQp({ quotes: [], projects: [] });
    setShowNoteInput(false);
    setNoteTitle('');
    setNoteText('');
    setContactModal({ open: false, contact: null });
    setPanelOpen(false);
    setPanelTab('quotes');
    setFocusOrder(null);
    setTaskSubject('');
    setTaskDesc('');
    setTaskDueDate('');
    setTaskPriority('Normal');
    setTaskStatus('Not Started');
    setTaskRelatedType('account');
    setTaskRelatedId('');
    setShowCrmModal(false);
    setCrmModalMode(null);
    setPendingQuote(null);
    setCrmQuoteForm(null);
    setShowQuoteModal(false);
    setQuotePrefillAccount(null);
    setShowAddCallModal(false);
    setQuotePrefillItems(null);
    setQuoteInitial(null);
    setCompletionResult(null);
    setQuoteStageOverrides({});
  };

  const applyResumeSnapshot = (snap) => {
    if (!snap) return;
    setView(snap.view || 'detail');
    setAccountQuery(snap.accountQuery || snap.selectedAccount?.name || '');
    setShowNoteInput(!!snap.showNoteInput);
    setNoteTitle(snap.noteTitle || '');
    setNoteText(snap.noteText || '');
    setTaskSubject(snap.taskSubject || '');
    setTaskDesc(snap.taskDesc || '');
    setTaskDueDate(snap.taskDueDate || '');
    setTaskPriority(snap.taskPriority || 'Normal');
    setTaskStatus(snap.taskStatus || 'Not Started');
    setTaskRelatedType(snap.taskRelatedType || 'account');
    setTaskRelatedId(snap.taskRelatedId || '');
    if (snap.selectedAccount) {
      setSelectedAccount(snap.selectedAccount);
      loadAccountData(snap.selectedAccount);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (resumeSnapshot) {
        applyResumeSnapshot(resumeSnapshot);
      } else {
        resetAll();
        setFocusOrder(initialOrder || null);
        if (initialAccount?.id) {
          // Defer so resetAll state updates are flushed first
          setTimeout(() => handleSelectAccount(initialAccount), 0);
        } else if (initialSearch) {
          // Open the named customer directly (exact name match), exactly like opening it from global search.
          setTimeout(() => handleOpenExactCustomer(initialSearch), 0);
        }
      }
    }
  }, [isOpen]);

  // Modal UI v2 — keep the Activity Bar's live snapshot for this activity fresh
  // as the user works, so navigating away and minimizing it preserves progress.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !selectedAccount) return;
    activityBar.updateActiveSnapshot({
      view, selectedAccount, accountQuery,
      showNoteInput, noteTitle, noteText,
      taskSubject, taskDesc, taskDueDate, taskPriority, taskStatus, taskRelatedType, taskRelatedId,
    }, {
      title: selectedAccount.name,
      subtitle: view === 'task' ? `Task for ${selectedAccount.name}` : selectedAccount.name,
    });
  }, [isOpen, modalUiV2, view, selectedAccount, accountQuery, showNoteInput, noteTitle, noteText, taskSubject, taskDesc, taskDueDate, taskPriority, taskStatus, taskRelatedType, taskRelatedId]);

  // Modal UI v2 keeps the header interactive above the overlay, so lock the page's
  // own scroll while open — otherwise scrolling past the modal's own bottom just
  // scrolls the dimmed page behind it instead of stopping cleanly at the modal.
  useEffect(() => {
    if (!isOpen || !modalUiV2) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen, modalUiV2]);


  // Customer Management fully covers the band under the sub-header, so the Open / Recent tabs are hidden while it is open —
  // including on its search screen, before a customer is picked (no activity is registered until then, which is why they
  // used to show above it when it was opened from Sales / CRM).
  useEffect(() => {
    if (!isOpen || !modalUiV2) return undefined;
    return activityBar.suppressBar();
  }, [isOpen, modalUiV2]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced account search
  useEffect(() => {
    if (!isOpen || view !== 'search') return;
    if (accountQuery.trim().length < 2) { setAccounts([]); return; }
    const t = setTimeout(() => runAccountSearch(accountQuery), 300);
    return () => clearTimeout(t);
  }, [accountQuery, isOpen, view]);

  // Fetch Zoho CRM users once when the task view is opened (cached in state)
  useEffect(() => {
    if (!isOpen || view !== 'task') return;
    if (zohoUsers.length > 0) return;
    setZohoUsersLoading(true);
    Promise.all([
      api.get(`${API}/zoho/crm-users`),
      api.get(`${API}/auth/me`),
    ]).then(([usersRes, meRes]) => {
      const users = usersRes.data?.users || [];
      setZohoUsers(users);
      const myEmail = meRes.data?.email || '';
      const meUser = users.find(u => (u.email || '').toLowerCase() === myEmail.toLowerCase());
      if (meUser && !taskOwnerId) setTaskOwnerId(meUser.id);
    }).catch(() => {}).finally(() => setZohoUsersLoading(false));
  }, [isOpen, view]);

  const runAccountSearch = async (q) => {
    if (!q?.trim()) return;
    setAccountLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-accounts`, { params: { q: q.trim() } });
      setAccounts(res.data?.accounts || []);
    } catch (_) { setAccounts([]); }
    finally { setAccountLoading(false); }
  };

  // Load contacts + notes + quotes/projects in parallel (3 calls total)
  const loadAccountData = async (account) => {
    setContactsLoading(true);
    setNotesLoading(true);
    setQpLoading(true);
    const [cRes, nRes, qpRes] = await Promise.allSettled([
      api.get(`${API}/zoho/account/${account.id}/contacts`),
      api.get(`${API}/zoho/account/${account.id}/notes`, { params: { limit: 15 } }),
      api.get(`${API}/zoho/account/${account.id}/quotes-and-projects`),
    ]);
    setContacts(cRes.status === 'fulfilled' ? (cRes.value.data?.contacts || []) : []);
    setNotes(nRes.status === 'fulfilled' ? (nRes.value.data?.notes || []) : []);
    setQp(qpRes.status === 'fulfilled'
      ? { quotes: qpRes.value.data?.quotes || [], projects: qpRes.value.data?.projects || [] }
      : { quotes: [], projects: [] });
    setContactsLoading(false);
    setNotesLoading(false);
    setQpLoading(false);
  };

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    setAccountQuery(account.name);
    setView('detail');
    setPanelOpen(false);
    setTaskSubject(`Follow up — ${account.name}`);
    loadAccountData(account);
    if (modalUiV2) {
      const snapshot = { selectedAccount: account, view: 'detail', accountQuery: account.name };
      activityBar.openActivity({ type: 'customer_management', activityId: account.id, title: account.name, subtitle: 'Customer Management', snapshot });
      activityBar.logRecentView({ type: 'customer_management', activityId: account.id, title: account.name, subtitle: 'Customer Management', snapshot });
    }
  };

  /** Called when a customer name is clicked (e.g. RecentQuotesPicker, Sales Hub) — resolves the
   * exact CRM account and opens it directly, no manual search step required. */
  const handleOpenExactCustomer = async (name) => {
    if (!name?.trim()) return;
    setView('search');
    setSelectedAccount(null);
    setAccountQuery(name);
    setAccountLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-accounts`, { params: { q: name.trim() } });
      const results = res.data?.accounts || [];
      const exact = results.find(a => (a.name || '').trim().toLowerCase() === name.trim().toLowerCase());
      if (exact) {
        handleSelectAccount(exact);
      } else if (results.length === 1) {
        handleSelectAccount(results[0]);
      } else {
        setAccounts(results);
      }
    } catch (_) {
      setAccounts([]);
    } finally {
      setAccountLoading(false);
    }
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) return toast.error('Note content is required');
    setSavingNote(true);
    try {
      await api.post(`${API}/zoho/create-note`, {
        account_id: selectedAccount.id,
        note_content: noteText.trim(),
        note_title: noteTitle.trim() || null,
      });
      setNotes(prev => [{
        id: `local-${Date.now()}`,
        title: noteTitle.trim() || 'Note from C30',
        content: noteText.trim(),
        created_at: new Date().toISOString(),
        owner_name: 'You',
      }, ...prev]);
      setCompletionResult({ type: 'note', summary: (noteTitle.trim() || noteText.trim()).slice(0, 90) });
      setShowNoteInput(false);
      setNoteText('');
      setNoteTitle('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save note');
    } finally { setSavingNote(false); }
  };

  const handleCreateTask = async () => {
    if (!taskSubject.trim()) return toast.error('Subject is required');
    setSavingTask(true);
    try {
      // Determine which record the task is linked to based on Related To
      let relatedId = selectedAccount.id;
      let relatedModule = 'Accounts';
      if (taskRelatedType === 'quote' && taskRelatedId) {
        relatedId = taskRelatedId;
        relatedModule = 'Quotes';
      } else if (taskRelatedType === 'project' && taskRelatedId) {
        relatedId = taskRelatedId;
        relatedModule = 'Potentials';
      }
      const res = await api.post(`${API}/zoho/create-task`, {
        record_id: relatedId,
        module: relatedModule,
        subject: taskSubject.trim(),
        description: taskDesc.trim() || '',
        due_date: taskDueDate || null,
        priority: taskPriority,
        status: taskStatus,
        owner_zoho_id: taskOwnerId || undefined,
      });
      setCompletionResult({ type: 'task', url: res.data?.task_url, summary: taskSubject.trim() });
      window.dispatchEvent(new Event('c30:tasks-changed'));
      setView('detail');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create task');
    } finally { setSavingTask(false); }
  };

  /** A contact was added or edited in the shared contact modal — update the list in place. */
  const handleContactSaved = (contact, mode) => {
    if (!contact?.id) return;
    if (mode === 'edit') {
      setContacts(prev => prev.map(c => (c.id === contact.id ? { ...c, ...contact } : c)));
    } else {
      setContacts(prev => [...prev, contact]);
    }
  };

  if (!isOpen) return null;

  const showTabs = view === 'detail' && !!selectedAccount && !completionResult;
  const showCrmLinkUrl = selectedAccount
    ? `https://crm.zoho.eu/crm/${process.env.REACT_APP_ZOHO_CRM_ORG || 'encon'}/tab/Accounts/${selectedAccount.id}`
    : null;
  const activePanelTab = SIDE_TABS.find(t => t.id === panelTab) || SIDE_TABS[0];

  return (
    <>
      {createPortal(
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-stretch justify-center ${modalUiV2 ? 'p-3' : 'p-3 pt-8'} ${modalUiV2 ? 'bg-black/50' : 'bg-black/70 backdrop-blur-sm'} ${v3Shell ? 'v3-root v3-modal-region' : ''}`}
      style={{
        ...(modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 80 }),
        // UI V3: the window sits in the content area (between the side bars, under the top bar and control bar), under the bars.
        ...(v3Shell ? { ...v3Shell.regionVars, top: v3Shell.regionTop, zIndex: 40 } : {}),
        // While a quote is open (Add Quote, Create Quote from the price list, Edit on a quote card) this window steps back and
        // the quote takes its place. Closing the quote brings this window back exactly as it was — same panel, same tab.
        ...(showQuoteModal ? { opacity: 0, pointerEvents: 'none' } : {}),
        transition: 'opacity 150ms ease',
      }}
      aria-hidden={showQuoteModal}
      data-testid="cnt-modal-overlay"
      data-v3-theme={v3Shell ? v3Shell.theme : undefined}
    >
      {/* Shell: [tab rail] [Customer Management] [slide-out panel]. Runs from the sub-header to the bottom of the screen. */}
      <div className="flex items-stretch justify-center w-full h-full min-w-0" data-testid="cnt-shell">

        {/* ── Tab rail — sits against the left edge of the modal ── */}
        {showTabs && (
          <div className="hidden md:flex flex-col gap-2 pt-20 flex-shrink-0" style={{ width: RAIL_W }} data-testid="cnt-tab-rail">
            {SIDE_TABS.map(({ id, label, Icon }) => {
              const active = panelOpen && panelTab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => openPanel(id)}
                  className={`flex flex-col items-center justify-center gap-2 py-4 rounded-l-2xl border-2 border-r-0 font-bold text-xs transition-colors ${
                    active ? 'text-white border-orange-500' : 'text-orange-700 bg-orange-50 border-orange-200 hover:bg-orange-100'
                  }`}
                  style={{ width: RAIL_W, ...(active ? { backgroundColor: ORANGE } : {}) }}
                  title={label}
                  data-testid={`cnt-tab-${id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span style={{ writingMode: 'vertical-rl', letterSpacing: '0.06em' }}>{label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Customer Management modal ── */}
        <div
          className="bg-gradient-to-br from-orange-50/40 via-white to-orange-50/20 rounded-3xl shadow-2xl h-full w-full border-2 border-orange-300 flex flex-col overflow-hidden min-w-0"
          style={{
            maxWidth: panelOpen ? HALF_WIDTH : MODAL_MAX_W,
            flex: panelOpen ? `1 1 ${HALF_WIDTH}` : `0 1 ${MODAL_MAX_W}px`,
            transition: 'max-width 300ms ease, flex-basis 300ms ease',
          }}
          data-testid="cnt-modal"
        >
        {/* ── Header: customer name (large) with "Customer Management" underneath, then the actions in a line ── */}
        <div className="v3-modal-head flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 bg-white/60 flex-shrink-0 flex-nowrap overflow-x-auto">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {view === 'task' && (
              <button
                onClick={() => setView('detail')}
                className="text-gray-400 hover:text-gray-700 rounded-full p-1 hover:bg-gray-100 flex-shrink-0"
                data-testid="cnt-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <h2
                className="text-lg sm:text-xl font-extrabold text-gray-900 tracking-tight leading-tight truncate"
                title={selectedAccount ? selectedAccount.name : undefined}
              >
                {selectedAccount ? selectedAccount.name : 'Customer Management'}
              </h2>
              {view === 'detail' && selectedAccount && !completionResult ? (
                <div className="flex items-center gap-3 mt-0.5">
                  <a
                    href={showCrmLinkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-semibold shrink-0"
                    data-testid="cnt-view-account-crm"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> View in CRM
                  </a>
                  <button
                    onClick={() => { setView('search'); setSelectedAccount(null); setAccountQuery(''); setAccounts([]); setPanelOpen(false); }}
                    className="text-xs text-gray-400 hover:text-gray-600 shrink-0 underline"
                    data-testid="cnt-change-account"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-gray-500 truncate">
                  {view === 'task'
                    ? 'Customer Management — Schedule Task'
                    : selectedAccount
                      ? 'Customer Management'
                      : 'Search an account to get started'}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-nowrap justify-end ml-auto">
            {view === 'detail' && selectedAccount && !completionResult && (
              <>
                <button
                  onClick={() => setShowNoteInput(v => !v)}
                  className={headerBtnClass}
                  style={{ borderColor: '#eab308', color: '#a16207', background: showNoteInput ? '#fef9c3' : 'white' }}
                  data-testid="cnt-add-note-btn"
                >
                  <StickyNote className="w-3.5 h-3.5" /> Add Note
                </button>
                <button
                  onClick={() => setView('task')}
                  className={`${headerBtnClass} border-purple-400 text-purple-700 hover:bg-purple-50`}
                  data-testid="cnt-schedule-task-btn"
                >
                  <CalendarClock className="w-3.5 h-3.5" /> Schedule Task
                </button>
                <button
                  onClick={pendingQuote ? handleSmartAddQuote : handleAddQuote}
                  className={`${headerBtnClass} ${
                    pendingQuote
                      ? 'border-orange-500 !bg-orange-500 text-white hover:!bg-orange-600'
                      : 'border-orange-400 text-orange-700 hover:bg-orange-50'
                  }`}
                  data-testid="cnt-add-quote-btn"
                  style={embeddedFromQuote ? { display: 'none' } : undefined}
                  title={pendingQuote ? `Add ${pendingQuote.orderNum || 'quote'} straight to CRM` : 'Create a new quote for this customer'}
                >
                  {pendingQuote ? (
                    <><Zap className="w-3.5 h-3.5" /> Add {pendingQuote.orderNum ? `${pendingQuote.orderNum} ` : ''}Quote</>
                  ) : (
                    <><FilePlus className="w-3.5 h-3.5" /> Add Quote</>
                  )}
                </button>
                <button
                  onClick={() => setShowAddCallModal(true)}
                  className={`${headerBtnClass} border-emerald-400 text-emerald-700 hover:bg-emerald-50`}
                  data-testid="cnt-add-call-btn"
                  title="Log a call for this customer"
                >
                  <Phone className="w-3.5 h-3.5" /> Add Call
                </button>
              </>
            )}
            <button
              onClick={() => { if (modalUiV2) activityBar.discardActive(); resetAll(); onClose?.(); }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-md transition-colors flex-shrink-0"
              data-testid="cnt-close-btn"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="v3-modal-body p-4 space-y-3 overflow-y-auto flex-1 min-h-0 [overscroll-behavior:contain]">

          {/* ── Completion banner ── */}
          {completionResult && (
            <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4" data-testid="cnt-completion-banner">
              <div className="flex items-center gap-2 text-emerald-700 font-bold mb-2">
                <CheckCircle2 className="w-5 h-5" />
                {completionResult.type === 'note' ? 'Note saved in Zoho CRM' : 'Task created in Zoho CRM'}
              </div>
              <div className="text-xs text-gray-700 mb-3 break-words">{completionResult.summary}</div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setCompletionResult(null)}
                  className="rounded-xl h-9 px-4 text-xs"
                  data-testid="cnt-completion-add-another"
                >
                  {completionResult.type === 'note' ? 'Add another note' : 'Back to account'}
                </Button>
                {completionResult.url && (
                  <a
                    href={completionResult.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-xl h-9 px-4 text-white text-xs font-semibold"
                    style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
                    data-testid="cnt-view-in-crm-link"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View in CRM
                  </a>
                )}
                <Button
                  onClick={() => { resetAll(); onClose?.(); }}
                  className="rounded-xl h-9 px-4 text-xs text-white"
                  style={{ backgroundColor: '#6b7280' }}
                  data-testid="cnt-completion-close"
                >
                  Close
                </Button>
              </div>
            </div>
          )}

          {/* ══ VIEW: SEARCH ══════════════════════════════════════════════════ */}
          {!completionResult && view === 'search' && (
            <div className={fieldCardClass}>
              {/* Recent customers — press one to open that customer */}
              {!showAddAccount && (
                <div className="mb-3 pb-3 border-b border-gray-100">
                  <RecentQuotesPicker variant="customers" onSelect={handleSelectRecentQuoteForSearch} onCustomerNameClick={handleOpenExactCustomer} onHistoryClick={item => setHistoryItem(item)} />
                </div>
              )}

              {/* Pending quote banner */}
              {pendingQuote && !showAddAccount && (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs mb-2">
                  <Zap className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  <span className="font-semibold text-blue-800 flex-1 truncate">
                    Quick-add ready: {pendingQuote.orderNum ? `${pendingQuote.orderNum} — ` : ''}{pendingQuote.customerName || 'Quote selected'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPendingQuote(null)}
                    className="text-blue-400 hover:text-blue-600 flex-shrink-0"
                    data-testid="cnt-clear-pending-quote"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                Search Account
              </Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={accountQuery}
                  onChange={(e) => { setAccountQuery(e.target.value); setAccounts([]); }}
                  placeholder="Type customer / company name…"
                  className={`flex-1 ${inputClass}`}
                  autoFocus
                  data-testid="cnt-account-search"
                />
                <Button
                  type="button"
                  onClick={() => runAccountSearch(accountQuery)}
                  disabled={accountLoading}
                  className="rounded-xl h-10 w-10 p-0"
                  style={{ backgroundColor: ORANGE }}
                >
                  {accountLoading
                    ? <Loader2 className="w-4 h-4 animate-spin text-white" />
                    : <Search className="w-4 h-4 text-white" />}
                </Button>
              </div>

              {(accountLoading || accounts.length > 0) && !showAddAccount && (
                <div className="border border-gray-200 rounded-xl divide-y max-h-60 overflow-y-auto mt-2 bg-white shadow-inner">
                  {accountLoading ? (
                    <div className="p-3 text-center text-gray-500 text-sm">Searching…</div>
                  ) : accounts.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => handleSelectAccount(a)}
                      className="w-full text-left p-3 hover:bg-orange-50 transition-colors"
                      data-testid={`cnt-account-option-${a.id}`}
                    >
                      <div className="font-semibold text-sm text-gray-900">{a.name}</div>
                      <div className="text-xs text-gray-500">
                        {[a.city, a.customer_code ? `#${a.customer_code}` : null].filter(Boolean).join(' · ')}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {!showAddAccount && (
                <button
                  type="button"
                  onClick={() => setShowAddAccount(true)}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700"
                  data-testid="cnt-add-account-toggle"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  Can't find them? Create new prospect account
                </button>
              )}

              {showAddAccount && (
                <AddProspectAccountInline
                  themeColor="orange"
                  onCancel={() => setShowAddAccount(false)}
                  onCreated={({ account, contact }) => {
                    const acc = { id: account.id, name: account.name };
                    setSelectedAccount(acc);
                    setAccountQuery(account.name);
                    if (contact?.id) {
                      setContacts([{
                        id: contact.id,
                        full_name: contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
                        first_name: contact.first_name,
                        last_name: contact.last_name,
                        email: contact.email,
                        phone: contact.phone,
                        mobile: contact.mobile,
                      }]);
                    }
                    setShowAddAccount(false);
                    setView('detail');
                    setTaskSubject(`Follow up — ${account.name}`);
                  }}
                />
              )}
            </div>
          )}

          {/* ══ VIEW: ACCOUNT DETAIL ══════════════════════════════════════════ */}
          {!completionResult && view === 'detail' && selectedAccount && (
            <>
              {/* ── Note input area (inline, toggled by the Add Note button in the header) ── */}
              {showNoteInput && (
                <div
                  className="rounded-xl border-2 border-yellow-300 bg-yellow-50/40 p-3 space-y-2"
                  data-testid="cnt-note-input-area"
                >
                  <Input
                    value={noteTitle}
                    onChange={(e) => setNoteTitle(e.target.value)}
                    placeholder="Note title (optional)"
                    className={inputClass}
                    data-testid="cnt-note-title"
                  />
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Enter note content…"
                    rows={4}
                    className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none resize-none"
                    data-testid="cnt-note-text"
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => { setShowNoteInput(false); setNoteText(''); setNoteTitle(''); }}
                      className="rounded-xl h-9 px-3 text-xs"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveNote}
                      disabled={savingNote || !noteText.trim()}
                      className="rounded-xl h-9 px-4 text-xs text-white font-semibold"
                      style={{ backgroundColor: ORANGE }}
                      data-testid="cnt-save-note-btn"
                    >
                      {savingNote
                        ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Saving…</>
                        : 'Save Note'}
                    </Button>
                  </div>
                </div>
              )}

              {/* ── Contacts — two columns; click a contact to edit it (also updates the CRM) ── */}
              <div className={fieldCardClass}>
                <div className="flex items-center gap-1.5 mb-2">
                  <UserRound className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Contacts</span>
                  {contacts.length > 0 && (
                    <span className="ml-auto text-[11px] text-gray-400">{contacts.length}</span>
                  )}
                </div>
                {contactsLoading ? (
                  <div className="text-xs text-gray-500 flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-xs text-gray-500 italic">No contacts found on this account.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {contacts.map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => setContactModal({ open: true, contact: c })}
                        className="text-left flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 hover:border-orange-300 hover:bg-orange-50/50 transition-colors"
                        title="Click to edit this contact"
                        data-testid={`cnt-contact-${c.id}`}
                      >
                        <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <UserRound className="w-3.5 h-3.5 text-orange-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-gray-900 truncate">
                            {c.full_name || c.email || c.id}
                          </div>
                          {c.title && (
                            <div className="text-[11px] text-gray-500 truncate">{c.title}</div>
                          )}
                          {(c.mobile || c.phone) && (
                            <div className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1 truncate">
                              <Phone className="w-3 h-3 flex-shrink-0" /> {c.mobile || c.phone}
                            </div>
                          )}
                          {c.email && (
                            <div className="text-[11px] text-blue-600 flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3 flex-shrink-0" /> {c.email}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setContactModal({ open: true, contact: null })}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700"
                  data-testid="cnt-add-contact-toggle"
                >
                  <UserPlus className="w-3.5 h-3.5" /> Add new contact
                </button>
              </div>

              {/* ── Quotes & Projects ── */}
              <div className={fieldCardClass}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Briefcase className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Quotes & Projects</span>
                </div>
                {qpLoading ? (
                  <div className="text-xs text-gray-500 flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                  </div>
                ) : (qp.quotes.length === 0 && qp.projects.length === 0) ? (
                  <div className="text-xs text-gray-500 italic">No quotes or projects found.</div>
                ) : (
                  <div className="space-y-2">
                    {(() => {
                      // Group quotes by project (Deal_Name link from Zoho)
                      const projectMap = new Map();
                      const standalone = [];
                      for (const q of qp.quotes) {
                        if (q.project_id) {
                          if (!projectMap.has(q.project_id)) {
                            projectMap.set(q.project_id, {
                              id: q.project_id,
                              name: q.project_name || 'Unnamed Project',
                              crm_url: q.project_crm_url || null,
                              quotes: [],
                            });
                          }
                          projectMap.get(q.project_id).quotes.push(q);
                        } else {
                          standalone.push(q);
                        }
                      }
                      const groups = [...projectMap.values()];

                      const renderQuoteRow = (q) => {
                        const currentStage = quoteStageOverrides[q.id] ?? q.stage;
                        const createdLabel = q.created_at
                          ? (() => { try { return new Date(q.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return ''; } })()
                          : '';
                        return (
                          <div
                            key={q.id}
                            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 hover:bg-blue-50/50 hover:border-blue-200 transition-all group"
                            data-testid={`cnt-quote-${q.id}`}
                          >
                            <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-3 h-3 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                {q.name || 'Untitled Quote'}
                              </div>
                              {createdLabel && (
                                <div className="text-[10px] text-gray-400 mt-0.5">{createdLabel}</div>
                              )}
                            </div>
                            <div className="w-[108px] flex-shrink-0 text-right">
                              <span className="text-xs font-semibold text-gray-700 tabular-nums">
                                {q.value != null ? `£${Number(q.value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                              </span>
                            </div>
                            <div className="w-[58px] flex-shrink-0 flex justify-center">
                              {q.has_pdf && (
                                <button
                                  type="button"
                                  className="flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 transition-colors cursor-pointer"
                                  title="Open attached PDF"
                                  data-testid={`cnt-quote-pdf-${q.id}`}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const res = await api.get(`${API}/zoho/quotes/${q.id}/pdf-download`, { responseType: 'blob' });
                                      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/pdf' });
                                      const blobUrl = window.URL.createObjectURL(blob);
                                      const win = window.open(blobUrl, '_blank');
                                      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
                                      if (!win) toast.error('Popup blocked — please allow popups for this site');
                                    } catch (err) {
                                      const msg = err?.response?.data
                                        ? await err.response.data.text?.() || err?.response?.data?.detail || 'Failed to load PDF'
                                        : 'Failed to load PDF';
                                      toast.error(typeof msg === 'string' ? msg : 'Failed to load PDF');
                                    }
                                  }}
                                >
                                  <Paperclip className="w-3 h-3" />
                                  PDF
                                </button>
                              )}
                            </div>
                            <div className="w-[162px] flex-shrink-0">
                              <StagePill
                                quoteId={q.id}
                                stage={currentStage}
                                onStageChange={handleQuoteStageChange}
                                testIdPrefix="cnt-quote-stage"
                              />
                            </div>
                            <a
                              href={q.crm_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-[11px] text-blue-500 hover:text-blue-700 flex-shrink-0"
                              title="Open in Zoho CRM"
                              data-testid={`cnt-quote-crm-link-${q.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        );
                      };

                      return (
                        <>
                          {/* ── Project groups first ── */}
                          {groups.map((group) => (
                            <div key={group.id} className="space-y-1">
                              <div className="flex items-center gap-1.5 px-1">
                                <Briefcase className="w-3 h-3 text-purple-500 flex-shrink-0" />
                                {group.crm_url ? (
                                  <a
                                    href={group.crm_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[11px] font-bold text-purple-700 uppercase tracking-wider truncate hover:text-purple-900 hover:underline"
                                    title="Open project in Zoho CRM"
                                  >
                                    {group.name}
                                  </a>
                                ) : (
                                  <span className="text-[11px] font-bold text-purple-700 uppercase tracking-wider truncate">
                                    {group.name}
                                  </span>
                                )}
                                {group.created_at && (
                                  <span className="text-[10px] text-gray-400 flex-shrink-0">
                                    {(() => { try { return new Date(group.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return ''; } })()}
                                  </span>
                                )}
                                <div className="flex-1 h-px bg-purple-200" />
                              </div>
                              <div className="space-y-1 pl-3 border-l-2 border-purple-100">
                                {group.quotes.map(renderQuoteRow)}
                              </div>
                            </div>
                          ))}

                          {/* ── Standalone quotes (not linked to any project) ── */}
                          {standalone.length > 0 && (
                            <div className="space-y-1">
                              {groups.length > 0 && (
                                <div className="flex items-center gap-1.5 px-1">
                                  <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                    Other Quotes
                                  </span>
                                  <div className="flex-1 h-px bg-gray-200" />
                                </div>
                              )}
                              <div className="space-y-1">
                                {standalone.map(renderQuoteRow)}
                              </div>
                            </div>
                          )}

                          {/* ── Potentials / Projects (standalone Zoho Potentials records) ── */}
                          {qp.projects.length > 0 && (
                            <div className="space-y-1">
                              {(groups.length > 0 || standalone.length > 0) && (
                                <div className="flex items-center gap-1.5 px-1">
                                  <Briefcase className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                                  <span className="text-[11px] font-semibold text-indigo-400 uppercase tracking-wider">
                                    Projects
                                  </span>
                                  <div className="flex-1 h-px bg-indigo-100" />
                                </div>
                              )}
                              <div className="space-y-1">
                                {qp.projects.map((p) => (
                                  <a
                                    key={p.id}
                                    href={p.crm_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 hover:bg-purple-50 hover:border-purple-200 transition-all group"
                                    data-testid={`cnt-project-${p.id}`}
                                  >
                                    <div className="w-6 h-6 rounded-md bg-purple-100 flex items-center justify-center flex-shrink-0">
                                      <Briefcase className="w-3 h-3 text-purple-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-semibold text-gray-900 truncate group-hover:text-purple-700">
                                        {p.name || 'Untitled Project'}
                                      </div>
                                      <div className="text-[11px] text-gray-500">
                                        {[
                                          p.stage,
                                          p.value != null ? `£${Number(p.value).toLocaleString('en-GB')}` : null,
                                          p.closing_date ? `Close: ${p.closing_date}` : null,
                                        ].filter(Boolean).join(' · ')}
                                      </div>
                                    </div>
                                    <ExternalLink className="w-3 h-3 text-gray-400 group-hover:text-purple-500 flex-shrink-0" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* ── Notes history ── */}
              <div className={fieldCardClass}>
                <button
                  className="w-full flex items-center gap-1.5"
                  onClick={() => setNotesExpanded(v => !v)}
                  data-testid="cnt-notes-toggle"
                >
                  <StickyNote className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Notes</span>
                  {notes.length > 0 && (
                    <span className="ml-1 text-[11px] bg-gray-200 text-gray-600 rounded-full px-1.5 py-0.5 font-semibold">
                      {notes.length}
                    </span>
                  )}
                  <span className="ml-auto">
                    {notesExpanded
                      ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                      : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                  </span>
                </button>

                {notesLoading ? (
                  <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                  </div>
                ) : notes.length === 0 ? (
                  <div className="text-xs text-gray-500 italic mt-2">No notes found.</div>
                ) : (
                  <>
                    {/* Latest note always visible */}
                    <NoteCard note={notes[0]} />
                    {/* Older notes shown when expanded */}
                    {notesExpanded && notes.slice(1).map((n) => (
                      <NoteCard key={n.id} note={n} />
                    ))}
                    {notes.length > 1 && (
                      <button
                        onClick={() => setNotesExpanded(v => !v)}
                        className="mt-1 text-xs text-orange-600 hover:text-orange-700 font-semibold flex items-center gap-1"
                        data-testid="cnt-notes-expand"
                      >
                        {notesExpanded
                          ? <><ChevronUp className="w-3 h-3" />Show less</>
                          : <><ChevronDown className="w-3 h-3" />View {notes.length - 1} more note{notes.length - 1 !== 1 ? 's' : ''}</>}
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* ══ VIEW: TASK FORM ═══════════════════════════════════════════════ */}
          {!completionResult && view === 'task' && (
            <div className="space-y-3" data-testid="cnt-task-form">
              <div className={fieldCardClass}>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Subject *</Label>
                <Input
                  value={taskSubject}
                  onChange={(e) => setTaskSubject(e.target.value)}
                  placeholder="Task subject…"
                  className={`mt-1.5 ${inputClass}`}
                  autoFocus
                  data-testid="cnt-task-subject"
                />
              </div>

              <div className={fieldCardClass}>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Description</Label>
                <textarea
                  value={taskDesc}
                  onChange={(e) => setTaskDesc(e.target.value)}
                  placeholder="Optional — notes or context for this task…"
                  rows={3}
                  className="w-full mt-1.5 border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none resize-none"
                  data-testid="cnt-task-desc"
                />
              </div>

              {/* Task Owner */}
              <div className={fieldCardClass}>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-1.5">
                  Task Owner
                </Label>
                {zohoUsersLoading ? (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 py-1">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading users…
                  </div>
                ) : (
                  <select
                    value={taskOwnerId}
                    onChange={(e) => setTaskOwnerId(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:outline-none"
                    data-testid="cnt-task-owner"
                  >
                    <option value="">— Select owner —</option>
                    {zohoUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.name}{u.email ? ` (${u.email})` : ''}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Related To */}
              <div className={fieldCardClass}>
                <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-1.5">
                  Related To
                </Label>
                <div className="flex gap-2">
                  {[
                    { value: 'account', label: 'Account' },
                    { value: 'quote',   label: 'Quote' },
                    { value: 'project', label: 'Project' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setTaskRelatedType(opt.value); setTaskRelatedId(''); }}
                      className="flex-1 rounded-xl py-1.5 text-xs font-semibold border-2 transition-colors"
                      style={taskRelatedType === opt.value
                        ? { background: ORANGE, borderColor: ORANGE, color: 'white' }
                        : { background: 'white', borderColor: '#e5e7eb', color: '#6b7280' }
                      }
                      data-testid={`cnt-task-related-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Sub-select for quote or project */}
                {taskRelatedType === 'quote' && qp.quotes.length > 0 && (
                  <select
                    value={taskRelatedId}
                    onChange={(e) => setTaskRelatedId(e.target.value)}
                    className="w-full mt-2 border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:outline-none"
                    data-testid="cnt-task-quote-select"
                  >
                    <option value="">— Select a quote —</option>
                    {qp.quotes.map(q => (
                      <option key={q.id} value={q.id}>{q.name}{q.stage ? ` · ${q.stage}` : ''}</option>
                    ))}
                  </select>
                )}
                {taskRelatedType === 'quote' && qp.quotes.length === 0 && (
                  <p className="text-xs text-gray-400 italic mt-1.5">No quotes found for this account.</p>
                )}

                {taskRelatedType === 'project' && qp.projects.length > 0 && (
                  <select
                    value={taskRelatedId}
                    onChange={(e) => setTaskRelatedId(e.target.value)}
                    className="w-full mt-2 border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:outline-none"
                    data-testid="cnt-task-project-select"
                  >
                    <option value="">— Select a project —</option>
                    {qp.projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}{p.stage ? ` · ${p.stage}` : ''}</option>
                    ))}
                  </select>
                )}
                {taskRelatedType === 'project' && qp.projects.length === 0 && (
                  <p className="text-xs text-gray-400 italic mt-1.5">No projects found for this account.</p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className={fieldCardClass}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-1.5">
                    Follow-up Date
                  </Label>
                  <input
                    type="date"
                    value={taskDueDate}
                    onChange={(e) => setTaskDueDate(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none"
                    data-testid="cnt-task-due-date"
                  />
                </div>
                <div className={fieldCardClass}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-1.5">
                    Priority
                  </Label>
                  <select
                    value={taskPriority}
                    onChange={(e) => setTaskPriority(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:outline-none"
                    data-testid="cnt-task-priority"
                  >
                    <option>Normal</option>
                    <option>High</option>
                    <option>Low</option>
                  </select>
                </div>
                <div className={fieldCardClass}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 block mb-1.5">
                    Status
                  </Label>
                  <select
                    value={taskStatus}
                    onChange={(e) => setTaskStatus(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:outline-none"
                    data-testid="cnt-task-status"
                  >
                    <option>Not Started</option>
                    <option>In Progress</option>
                    <option>Completed</option>
                    <option>Waiting on someone else</option>
                    <option>Deferred</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => setView('detail')}
                  className="rounded-xl h-11 px-4"
                  data-testid="cnt-task-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateTask}
                  disabled={savingTask || !taskSubject.trim()}
                  className="rounded-xl h-11 px-6 text-white font-semibold"
                  style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)' }}
                  data-testid="cnt-create-task-btn"
                >
                  {savingTask
                    ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating…</>
                    : <><CalendarClock className="w-4 h-4 mr-2" />Create Task</>}
                </Button>
              </div>
            </div>
          )}
        </div>
        </div>

        {/* ── Slide-out panel (Quotes / Orders / Pricing) — grows out to the RIGHT of the Customer Management modal ── */}
        {showTabs && (
          <div
            className="hidden md:block flex-shrink-0 h-full rounded-3xl border-2 border-orange-300 bg-white shadow-2xl overflow-hidden"
            style={{
              width: panelOpen ? HALF_WIDTH : 0,
              minWidth: panelOpen ? 320 : 0,
              marginLeft: panelOpen ? SHELL_GAP : 0,
              opacity: panelOpen ? 1 : 0,
              borderWidth: panelOpen ? 2 : 0,
              transition: 'width 300ms ease, margin 300ms ease, opacity 200ms ease',
            }}
            data-testid="cnt-side-panel"
            aria-hidden={!panelOpen}
          >
            {panelOpen && selectedAccount && (
              <>
                {activePanelTab.id === 'quotes' && (
                  <CustomerQuotesPanel
                    account={selectedAccount}
                    user={me}
                    onAddQuote={embeddedFromQuote ? null : handleAddQuote}
                    onOpenQuote={handleOpenQuoteFromPanel}
                    refreshKey={quoteTick}
                  />
                )}
                {activePanelTab.id === 'orders' && (
                  <CustomerOrdersPanel
                    account={selectedAccount}
                    focusOrderId={focusOrder?.orderId || null}
                    onFocusConsumed={() => setFocusOrder(null)}
                    orderNumberTabs={transportOrderNumbers}
                  />
                )}
                {activePanelTab.id === 'pricing' && (
                  <CustomerPricingPanel
                    account={selectedAccount}
                    onCreateQuote={embeddedFromQuote ? null : handleCreateQuoteFromPriceList}
                    refreshKey={quoteTick}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )}

  {/* ── Add / edit contact — one modal for both ── */}
  <CustomerContactModal
    isOpen={contactModal.open && !!selectedAccount}
    onClose={() => setContactModal({ open: false, contact: null })}
    accountId={selectedAccount?.id}
    accountName={selectedAccount?.name || ''}
    contact={contactModal.contact}
    onSaved={handleContactSaved}
  />

  {/* ── Create Quote (orange Add Quote button) — same modal as the dashboard's "Create New" Quotes flow ── */}
  {showQuoteModal && QuotesMod && selectedAccount && createPortal(
    <QuotesMod.Modal
      isOpen={true}
      mode="create"
      user={me}
      fromCustomerManagement={true}
      prefillAccount={quotePrefillAccount}
      prefillItems={quotePrefillItems}
      initialQuote={quoteInitial}
      onClose={() => {
        setShowQuoteModal(false);
        setQuotePrefillAccount(null);
        setQuotePrefillItems(null);
        setQuoteInitial(null);
        setQuoteTick((t) => t + 1);
      }}
    />,
    document.body,
  )}

  {/* ── Add Call — same modal used elsewhere, pre-filled with this customer ── */}
  {showAddCallModal && selectedAccount && (
    <AddCallToCrmModal
      isOpen={showAddCallModal}
      onClose={() => setShowAddCallModal(false)}
      prefillAccount={{ account: { id: selectedAccount.id, name: selectedAccount.name } }}
    />
  )}

  {/* ── AddToCrmModal — kept for the Smart-Add quote flow only (Add Quote to CRM directly from a picked Recent Quote) ── */}
  {showCrmModal && selectedAccount && (
    <AddToCrmModal
      isOpen={showCrmModal}
      onClose={handleCrmModalClose}
      quoteForm={crmQuoteForm || null}
      pdfFile={null}
      subtotal={crmQuoteForm
        ? (crmQuoteForm.items || []).reduce(
            (s, i) => s + (parseFloat(i.unit_price) || 0) * (parseFloat(i.quantity) || 0), 0
          )
        : 0}
      fastTrack={true}
      initialMode={crmModalMode}
      initialAccount={{ id: selectedAccount.id, name: selectedAccount.name }}
      zIndex={90}
      onCrmSuccess={() => { setPendingQuote(null); setCrmQuoteForm(null); }}
    />
  )}
  {historyItem && (
    <QuoteVersionHistoryModal
      quoteId={historyItem.id}
      customerName={(historyItem.quote_data || historyItem.email_data || {}).customer_name || historyItem.user_label || ''}
      quoteNumber={(historyItem.quote_data || {}).quote_number || ''}
      onClose={() => setHistoryItem(null)}
    />
  )}
</>
  );
};

export default ContactNotesTasksModal;
